import { selectIsWhatsAppInfobipLocalesEnabled } from 'core/userContext';
import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { Add16 } from 'ui/icons/action/Add16';
import { ListEmptyTableState, NoResult } from 'ui/illustrations';
import { pxToRem } from 'utils/convert';
import { FIELD_NAMES } from '../../constants';
import {
  MODAL_NAMES,
  ModalState,
  ModalStatePayload,
  ProjectSettingsFormSchemaType,
  useProjectSettingsModalsContext,
} from '../../contexts';
import {
  List,
  ListBanner,
  ListBody,
  ListContextProvider,
  ListFooter,
  ListHeader,
  StyledPlaceholderState,
  useListContext,
  Wrapper,
} from '../List';
import { LocaleListRow } from './LocaleListRow';
import { LocalesTable } from './LocalesTable';

const { LOCALES, MAPPED_LOCALES } = FIELD_NAMES;

const StyledWrapper = styled(Wrapper)`
  grid-template-rows: 
  ${`${pxToRem(72)}  
  auto 
  fit-content(${pxToRem(340)}) 
  fit-content(${pxToRem(52)})`};
`;

const StyledList = ({ children }: { children: React.ReactNode }) => (
  <StyledWrapper role="list">{children}</StyledWrapper>
);

const AddButton = ({
  isWhatsAppInfobipLocalesEnabled,
}: { isWhatsAppInfobipLocalesEnabled: boolean }) => {
  const { showModal } = useProjectSettingsModalsContext();
  return (
    <SecondaryButton
      size={BUTTON_SIZES.LARGE}
      type="button"
      onClick={() =>
        showModal({
          name: isWhatsAppInfobipLocalesEnabled ? MODAL_NAMES.WA_LOCALE : MODAL_NAMES.ADD_LOCALE,
        })
      }
    >
      <Add16 />
      Add locale
    </SecondaryButton>
  );
};

const FilteredLocalesList = ({
  filteredItems,
  defaultLocale,
}: { filteredItems: string[]; defaultLocale: string }) => {
  const { showModal } = useProjectSettingsModalsContext();

  return (
    <MemoLocalesList
      filteredItems={filteredItems}
      defaultLocale={defaultLocale}
      showModal={showModal}
    />
  );
};

const MemoLocalesList = memo(
  ({
    filteredItems,
    defaultLocale,
    showModal,
  }: {
    filteredItems: string[];
    defaultLocale: string;
    showModal: (modalState: ModalState<ModalStatePayload>) => void;
  }) => {
    return filteredItems.map((locale, index) => (
      <LocaleListRow
        /**
         * key is used to identify the row in the table.
         *
         * NOTE: There are cases where the same locale
         * is present multiple times in the filteredItems array, which causes unintended
         * side effects. That's why we're using the index as part of the key.
         */
        key={`row-${locale}-${index}`}
        locale={locale}
        isDefault={locale === defaultLocale}
        showModal={showModal}
      />
    ));
  },
);

const BaseLocalesList = () => {
  const isWhatsAppInfobipLocalesEnabled = useSelector(selectIsWhatsAppInfobipLocalesEnabled);
  const { items = [], filteredItems, hasNoItems, hasNoMatchingItems } = useListContext<string>();
  const { watch } = useFormContext<ProjectSettingsFormSchemaType>();
  const mappedLocales = watch(MAPPED_LOCALES);

  /**
   *  The state for default locale is maintained via the order of the values in
   *  project.extendeProjectSettings.locales: the first item is the default.
   */
  const defaultLocale = items[0];

  const ListContainer = isWhatsAppInfobipLocalesEnabled ? StyledList : List;

  return (
    <ListContainer>
      <ListHeader>
        <AddButton isWhatsAppInfobipLocalesEnabled={isWhatsAppInfobipLocalesEnabled} />
      </ListHeader>
      {isWhatsAppInfobipLocalesEnabled && mappedLocales.length !== items.length && (
        <ListBanner
          actionTitle="Learn more"
          actionUrl="https://developers.facebook.com/docs/whatsapp/business-management-api/message-templates/supported-languages"
          description="Map your locale codes to languages to begin sending WhatsApp messages."
        />
      )}
      <ListBody data-empty={hasNoItems || hasNoMatchingItems}>
        {match({ hasNoItems, hasNoMatchingItems })
          .with({ hasNoItems: true }, () => (
            <StyledPlaceholderState title="No locales yet" Illustration={ListEmptyTableState}>
              Create your first locale to get started.
            </StyledPlaceholderState>
          ))
          .with({ hasNoMatchingItems: true }, () => (
            <StyledPlaceholderState title="No matching results" Illustration={NoResult}>
              Try searching by a different keyword.
            </StyledPlaceholderState>
          ))
          .otherwise(() => (
            <>
              {isWhatsAppInfobipLocalesEnabled ? (
                <LocalesTable
                  filteredItems={filteredItems}
                  mappedLocales={mappedLocales}
                  defaultLocale={defaultLocale}
                />
              ) : (
                <FilteredLocalesList filteredItems={filteredItems} defaultLocale={defaultLocale} />
              )}
            </>
          ))}
      </ListBody>
      <ListFooter />
    </ListContainer>
  );
};

const localesFilter = (item: string, query: string) =>
  item?.toLowerCase()?.includes(query?.toLowerCase());

export const LocalesList = () => {
  const { watch } = useFormContext<ProjectSettingsFormSchemaType>();

  const locales = watch(LOCALES);

  return (
    <ListContextProvider items={locales} filterFn={localesFilter}>
      <BaseLocalesList />
    </ListContextProvider>
  );
};
